import axios from 'axios'
import messenger from './Messenger'

import {con, repository} from './messages/index.js'
import {
  SessionNotFoundError,
  SessionNotStartedError,
  SessionExpiredError,
  SessionClosedError,
  PartyHasAlreadyJoinedError,
  UserNotAuthorizedError,
  UserDisjoinError,
  UserNotFoundError,
  PinNotFoundError,
} from './errors/index.js'
import classifyJoinError from './errors/classifyJoinError.js'
import {useSessionStore} from 'app/src/stores/index.js'
import {StrategyType} from './StrategyType'
import {mediator} from './Mediator.js'
import { jsonRpc } from './JsonRpc'

export default class Session {
  constructor() {
    this.participants = []
    this.user = null
    this.callId = null
    this.sessionInfoResolver = null;
    this.sessionInfo = new Promise(resolve => this.sessionInfoResolver = resolve)
    this.strategy = StrategyType.P2P
    this.recorderReference = null
    jsonRpc.on('con:joined', this.participantJoinedSessionHandler)
    jsonRpc.on('con:disjoined', this.participantDisjoinedSessionHandler)
  }

  getApiSessionInfo(endpoint, appType, key, callId) {
    endpoint = endpoint.replace('{{callId}}', callId).replace('{{key}}', key)
    this.callId = callId

    return axios
      .get(endpoint)
      .then(async ({data: response}) => {
        if (response.user?.party === 'assistant' && response.lang) {
          messenger.emit('session:language:change', {language: response.lang})
        }

        const {user} = response
        if (!this.authorizeUser(appType, user)) {
          console.info('plt:info - user is not authorized to join', {appType, user})
          throw new UserNotAuthorizedError(`User is not authorized to join ${appType} app`)
        }

        this.sessionInfoResolver(response)
        this.user = {...user, key}
        this.user.id = this.user.id.toString()
        this.strategy = await this.getStrategy()
        return response
      })
      .catch(error => {
        const errorCode = error.response?.data?.errorCode
        const errorData = error.response?.data

        switch (errorCode) {
          case 'sessionNotStarted':
            console.info('plt:info - session is not valid (not_started)', errorData)
            throw new SessionNotStartedError(errorData?.error, errorData)

          case 'sessionNotFound':
            console.info('plt:info - session is not valid (not_found)', errorData)
            throw new SessionNotFoundError(errorData?.error, errorData)

          case 'sessionExpired':
            console.info('plt:info - session is not valid (expired)', errorData)
            throw new SessionExpiredError(errorData?.error, errorData)

          case 'sessionClosed':
            console.info('plt:info - session is not valid (closed)', errorData)
            throw new SessionClosedError(errorData?.error, errorData)

          case 'userNotFound':
            console.info('plt:info - user not found', errorData)
            throw new UserNotFoundError('User not found', errorData)

          default:
            throw error
        }
      })
  }

  static resolvePin(endpoint, pin) {
    const url = endpoint.replace('{{pin}}', pin)
    return axios
      .get(url)
      .then(({ data }) => data)
      .catch(error => {
        if (error.response?.status === 404) {
          throw new PinNotFoundError(error.response?.data?.message, error.response?.data)
        }

        throw error
      })
  }

  authorizeUser(appId, user) {
    const {party} = user
    return (
      (party === 'client' && appId === 'client') ||
      ((party === 'observer' || party === 'assistant') && appId === 'assistant')
    )
  }

  join() {
    return new Promise((resolve, reject) => {
      const {
        user: {id, key, party},
        callId,
      } = this
      mediator.send(con.joinMessage(id, key, party, callId), (error, response) => {
        if (error) {
          const joinError = classifyJoinError(error)
          if (joinError instanceof PartyHasAlreadyJoinedError) {
            console.info('con:join - user is already joined', error)
          } else if (joinError instanceof SessionExpiredError) {
            console.info('con:join - session expired', error)
          } else {
            console.info('con:join', error)
          }
          reject(joinError)
          return
        }
        const {...capabilities} = response
        this.configuration = capabilities
        console.info(`con:join - user joined ${JSON.stringify(this.user)}`)
        this.getJoin()
        this.getChat()
        this.getTransform()
        resolve({
          user: this.user,
          configuration: this.configuration,
        })
      })
    })
  }

  disjoin() {
    return mediator.sendAndWait(con.disjoinMessage())
  }

  getJoin() {
    mediator.send(repository.fetchEventsMessage('join'), (error, response) => {
      if (error) {
        return
      }

      const messages = JSON.parse(response)
      if (messages.length > 0 && messages[0].timestamp) {
        const date = new Date(messages[0].timestamp * 1000)
        messenger.emit('session:time', {timestamp: date.getTime()})
      }
    })
  }

  async getStrategy() {
    const client = participant => participant.party === 'client'
    const assistant = participant => participant.party === 'assistant'
    const sessionInfo = await this.sessionInfo;
    const hasClient = !!sessionInfo.participants.find(client)
    const hasAssistant = !!sessionInfo.participants.find(assistant)
    const strategy =
      hasClient && hasAssistant && sessionInfo.participants.length === 2 ? StrategyType.P2P : StrategyType.SFU
    return strategy
  }

  getChat() {
    mediator.send(repository.fetchEventsMessage('chat'), (error, response) => {
      if (error) {
        return
      }
      messenger.emit('chat:history', {history: response})
    })
  }

  getTransform() {
    mediator.send(repository.fetchEventsMessage('transform'), (error, response) => {
      if (error) {
        return
      }
      messenger.emit('transform:history', {history: response})
    })
  }

  canStart() {
    const partyJoined = party =>
      !!this.participants.find(participant => participant.party === party) || this.user.party === party
    if (partyJoined('client') && partyJoined('assistant')) {
      return true
    }
    return false
  }

  participantJoinedSessionHandler = async event => {
    const {id: participantId} = event.detail
    const sessionInfo = await this.sessionInfo;
    const participant = sessionInfo.participants.find(
      participant => Number(participant.id) === Number(participantId)
    )
    this.participants.push(participant)
    console.info(`con:joined - participant joined ${JSON.stringify(participant)}`)
    messenger.emit('session:participant:joined', participant)
    const session = useSessionStore()
    participant.status = 'connecting'
    session.addParticipant(participant)
  }

  participantDisjoinedSessionHandler = event => {
    const {id: participantId, isAlreadyDisJoined} = event.detail
    const session = useSessionStore()
    const participant = session.participants.find(participant => Number(participant.id) === Number(participantId)) || {}
    this.participants = this.participants.filter(participant => Number(participant.id) !== Number(participantId))
    console.info(`con:disjoined - participant disjoined ${JSON.stringify(participant)}`)

    participant['isAlreadyDisJoined'] = isAlreadyDisJoined
    messenger.emit('session:participant:disjoined', Object.assign({}, participant))
  }

  setRecorderReference(reference) {
    this.recorderReference = reference
  }

  clearRecorderReference() {
    this.recorderReference = null
  }

  getReference = () => this.recorderReference || this.configuration.reference
}
